import { useEffect, useState } from 'react';
import { api, readErr } from '@/lib/api';
import { Card } from '@/components/ui/card';
import {
    Users as UsersIcon, MessageSquare, CalendarClock, TrendingUp,
    Star, Clock, Wallet,
} from 'lucide-react';

function StatCard({ icon: Icon, label, value, sub, testId, accent = 'marigold' }) {
    const ring = accent === 'marigold' ? 'border-marigold/20 bg-marigold/10 text-marigold'
                : accent === 'emerald' ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400'
                : 'border-slate-500/30 bg-slate-500/10 text-slate-300';
    return (
        <Card className="rounded-sm bg-elevated border-marigold/10 p-5" data-testid={testId}>
            <div className="flex items-start justify-between">
                <div>
                    <div className="overline">{label}</div>
                    <div className="font-serif text-3xl text-parchment mt-2">{value}</div>
                    {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
                </div>
                <div className={`w-9 h-9 rounded-sm border flex items-center justify-center ${ring}`}>
                    <Icon size={16} />
                </div>
            </div>
        </Card>
    );
}

function formatDuration(sec) {
    const s = Math.round(Number(sec || 0));
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    const r = s % 60;
    return r ? `${m}m ${r}s` : `${m}m`;
}

function formatInr(paise) {
    return `₹${(Number(paise || 0) / 100).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
}

export default function AdminOverview() {
    const [stats, setStats] = useState(null);
    const [ratings, setRatings] = useState(null);
    const [err, setErr] = useState(null);
    const [phase, setPhase] = useState('loading');  // 'loading' | 'slow' | 'done' | 'error'

    const fetchStats = () => {
        setErr(null);
        setStats(null);
        setRatings(null);
        setPhase('loading');
        Promise.all([
            api.get('/admin/stats'),
            // Pull up to 25 recent ratings, low-first (sorted by rating
            // asc on the server). Low ratings carry comments worth
            // triaging; high ratings are also surfaced so the panel
            // never feels empty.
            api.get('/admin/chat/ratings?limit=25'),
        ])
            .then(([s, r]) => { setStats(s.data); setRatings(r.data.ratings || []); setPhase('done'); })
            .catch(e => { setErr(readErr(e, 'Failed to load')); setPhase('error'); });
    };

    useEffect(() => {
        fetchStats();
        // Round 37 — if /admin/stats hasn't returned in 15s, surface a
        // diagnostic state instead of leaving the admin staring at
        // "Loading…" forever (which is what production was doing when
        // some upstream API call hung).
        const t = setTimeout(() => setPhase(p => (p === 'loading' ? 'slow' : p)), 15000);
        return () => clearTimeout(t);
    }, []);

    if (err) {
        return (
            <div className="p-8 space-y-3 text-rose-400" data-testid="overview-error">
                <div className="font-medium">Couldn't load dashboard stats.</div>
                <div className="text-sm text-slate-400">{err}</div>
                <button onClick={fetchStats} className="mt-2 px-4 py-2 text-sm bg-marigold text-obsidian rounded-sm" data-testid="overview-retry">Retry</button>
            </div>
        );
    }
    if (!stats) {
        return (
            <div className="p-8 space-y-3 text-slate-400" data-testid="overview-loading">
                <div>Loading…</div>
                {phase === 'slow' && (
                    <div className="text-xs text-amber-400 max-w-md">
                        Stats are taking longer than usual. The admin API may be slow
                        or blocked. Try retrying, or open DevTools → Network and check
                        the status of <code className="text-amber-300">/api/admin/stats</code>.
                        <button
                            onClick={fetchStats}
                            className="block mt-3 px-4 py-2 text-xs bg-marigold text-obsidian rounded-sm"
                            data-testid="overview-retry-slow">
                            Retry now
                        </button>
                    </div>
                )}
            </div>
        );
    }

    const maxBar = Math.max(1, ...stats.timeseries.map(t => t.count));

    return (
        <div className="p-6 lg:p-10 space-y-8" data-testid="admin-overview">
            <div>
                <div className="overline">Overview</div>
                <h1 className="font-serif text-3xl text-parchment mt-2">At a glance.</h1>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard
                    testId="stat-users"
                    icon={UsersIcon}
                    label="Users"
                    value={stats.users.total}
                    sub={`+${stats.users.today} today · +${stats.users.week} this week`}
                />
                <StatCard
                    testId="stat-sessions"
                    icon={MessageSquare}
                    label="Chat sessions"
                    value={stats.sessions?.total ?? 0}
                    sub={`+${stats.sessions?.today ?? 0} today · +${stats.sessions?.week ?? 0} this week`}
                />
                <StatCard
                    testId="stat-avg-duration"
                    icon={Clock}
                    label="Avg session"
                    value={formatDuration(stats.sessions?.avg_duration_seconds)}
                    sub={`Across ${stats.sessions?.completed_count ?? 0} completed sessions`}
                />
                <StatCard
                    testId="stat-avg-rating"
                    icon={Star}
                    label="Avg rating"
                    value={(stats.ratings?.count ?? 0) > 0
                        ? `${stats.ratings.avg.toFixed(2)} ★`
                        : '—'}
                    sub={`Across ${stats.ratings?.count ?? 0} rated sessions`}
                    accent={stats.ratings?.avg >= 4 ? 'emerald' : 'marigold'}
                />
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard
                    testId="stat-questions"
                    icon={MessageSquare}
                    label="Questions (legacy)"
                    value={stats.questions.total}
                    sub={`+${stats.questions.today} today · +${stats.questions.week} week`}
                    accent="slate"
                />
                <StatCard
                    testId="stat-revenue-inr"
                    icon={TrendingUp}
                    label="Revenue (INR)"
                    value={`₹${stats.revenue.inr_total.total.toLocaleString('en-IN')}`}
                    sub={`₹${stats.revenue.inr_month.total.toLocaleString('en-IN')} last 30d · ${stats.revenue.inr_total.count} txn`}
                />
                <StatCard
                    testId="stat-revenue-usd"
                    icon={TrendingUp}
                    label="Revenue (USD)"
                    value={`$${Number(stats.revenue?.usd_total?.total ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                    sub={`$${Number(stats.revenue?.usd_month?.total ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} last 30d · ${stats.revenue?.usd_total?.count ?? 0} txn`}
                    accent="emerald"
                />
                <StatCard
                    testId="stat-wallet-spend"
                    icon={Wallet}
                    label="Wallet spend"
                    value={`₹${(stats.revenue?.wallet_spend_inr ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`}
                    sub="Chat + BTR + Compat deductions"
                />
                <StatCard
                    testId="stat-consultations"
                    icon={CalendarClock}
                    label="Consultations"
                    value={stats.consultations.total}
                    sub={`${stats.consultations.active} active`}
                />
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
                <Card className="rounded-sm bg-elevated border-marigold/10 p-6">
                    <div className="overline mb-4">Daily questions · last 14d</div>
                    {stats.timeseries.length === 0 ? (
                        <div className="text-sm text-slate-500">No questions yet.</div>
                    ) : (
                        <div data-testid="timeseries-chart">
                            <div className="flex items-end gap-1 h-32 border-b border-marigold/10">
                                {stats.timeseries.map(t => {
                                    const pct = Math.max(8, (t.count / maxBar) * 100);
                                    return (
                                        <div
                                            key={t.date}
                                            className="flex-1 bg-marigold/40 hover:bg-marigold rounded-t-sm transition-colors relative group"
                                            style={{ height: `${pct}%` }}
                                            title={`${t.date}: ${t.count}`}
                                        >
                                            <div className="absolute -top-5 left-1/2 -translate-x-1/2 text-[10px] text-marigold opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                                                {t.count}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                            <div className="flex gap-1 mt-2">
                                {stats.timeseries.map(t => (
                                    <div key={t.date} className="flex-1 text-[9px] text-slate-500 text-center">
                                        {t.date.slice(5)}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </Card>

                <Card className="rounded-sm bg-elevated border-marigold/10 p-6">
                    <div className="overline mb-4">Questions by category</div>
                    {stats.categories.length === 0 ? (
                        <div className="text-sm text-slate-500">No data.</div>
                    ) : (
                        <div className="space-y-3" data-testid="categories-chart">
                            {stats.categories.map(c => {
                                const pct = (c.count / stats.questions.total) * 100;
                                return (
                                    <div key={c.category}>
                                        <div className="flex justify-between text-sm">
                                            <span className="text-parchment capitalize">{c.category}</span>
                                            <span className="text-slate-500">{c.count} · {pct.toFixed(0)}%</span>
                                        </div>
                                        <div className="h-1.5 bg-obsidian rounded-full overflow-hidden mt-1">
                                            <div className="h-full bg-marigold" style={{ width: `${pct}%` }} />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </Card>
            </div>

            {/* Rating distribution + recent low-first ratings list.
                The histogram shows the score shape across all rated
                sessions; the table shows the latest 25 ratings sorted
                low-first so churn-risk comments are at the top. */}
            <div className="grid lg:grid-cols-3 gap-6">
                <Card className="rounded-sm bg-elevated border-marigold/10 p-6" data-testid="rating-distribution">
                    <div className="overline mb-4">Rating distribution</div>
                    {(stats.ratings?.count ?? 0) === 0 ? (
                        <div className="text-sm text-slate-500">No ratings yet.</div>
                    ) : (
                        <div className="space-y-2.5">
                            {[5, 4, 3, 2, 1].map((n) => {
                                const c = Number(stats.ratings.distribution?.[String(n)] || 0);
                                const total = stats.ratings.count || 1;
                                const pct = Math.round((c / total) * 100);
                                const tone = n >= 4 ? 'bg-emerald-400' : n === 3 ? 'bg-marigold' : 'bg-rose-500';
                                return (
                                    <div key={n} className="flex items-center gap-2 text-xs">
                                        <span className="w-12 text-slate-300 flex items-center gap-1">
                                            {n} <Star size={11} className="text-marigold fill-marigold" />
                                        </span>
                                        <div className="flex-1 h-2 bg-obsidian rounded-full overflow-hidden">
                                            <div className={`h-full ${tone}`} style={{ width: `${Math.max(pct, c > 0 ? 4 : 0)}%` }} />
                                        </div>
                                        <span className="w-12 text-right text-slate-400 tabular-nums">{c} · {pct}%</span>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </Card>

                <Card className="rounded-sm bg-elevated border-marigold/10 p-6 lg:col-span-2" data-testid="recent-ratings">
                    <div className="flex items-baseline justify-between mb-4">
                        <div className="overline">Recent ratings · low-first</div>
                        <div className="text-[11px] text-slate-500">Latest {ratings?.length || 0}</div>
                    </div>
                    {(!ratings || ratings.length === 0) ? (
                        <div className="text-sm text-slate-500">No ratings yet.</div>
                    ) : (
                        <div className="space-y-3 max-h-96 overflow-y-auto pr-1">
                            {ratings.map((r) => {
                                const tone = r.rating >= 4 ? 'border-emerald-500/20 bg-emerald-500/5'
                                          : r.rating === 3 ? 'border-marigold/20 bg-marigold/5'
                                          : 'border-rose-500/30 bg-rose-500/5';
                                return (
                                    <div
                                        key={r.id || r.session_id}
                                        data-testid={`rating-row-${r.session_id}`}
                                        className={`rounded-sm border ${tone} p-3 text-sm`}
                                    >
                                        <div className="flex items-baseline justify-between gap-3">
                                            <div className="flex items-center gap-2">
                                                <div className="font-serif text-lg text-parchment">
                                                    {r.rating} ★
                                                </div>
                                                <div className="text-xs text-slate-400 truncate">
                                                    {r.user_name || r.user_email || '(unknown user)'}
                                                </div>
                                            </div>
                                            <div className="text-[11px] text-slate-500 shrink-0">
                                                {formatDuration(r.duration_seconds)} · {formatInr(r.total_spent_paise)}
                                            </div>
                                        </div>
                                        {r.first_question && (
                                            <div className="text-xs text-slate-400 italic mt-1 line-clamp-2">
                                                &ldquo;{r.first_question}&rdquo;
                                            </div>
                                        )}
                                        {r.comment && (
                                            <div className="text-xs text-rose-200/90 mt-2 leading-relaxed">
                                                <span className="text-rose-400/90 font-medium">Why: </span>
                                                {r.comment}
                                            </div>
                                        )}
                                        <div className="text-[10px] text-slate-600 mt-1.5">
                                            {(r.updated_at || r.created_at || '').slice(0, 19).replace('T', ' ')}
                                            {r.user_email && (
                                                <span className="ml-2 text-slate-500">· {r.user_email}</span>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </Card>
            </div>
        </div>
    );
}
